import { api } from "../base";

interface AccountManagerAccount {
  name: string;
  display_name: string;
}

export interface ClstAccount {
  name: string;
  display_name: string;
}

export interface CustomerConfiguration {
  counterparty: string;
  all_in_tier: string | null;
  fee_tier: string | null;
  symbol_group: string | null;
  mode: string | null;
  update_user: string | null;
  revision: number | null;
}

interface RawCustomerConfiguration {
  Counterparty: string;
  AllInTier?: string | null;
  FeeTier?: string | null;
  SymbolGroup?: string | null;
  Mode?: string | null;
  UpdateUser?: string | null;
  Revision?: number | null;
}

interface SelectOption {
  value: string;
  label: string;
}

const accountManagerEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Fetches accounts from account-manager (select options). */
    getAccountManagerAccounts: build.query<SelectOption[], { name?: string }>({
      query: ({ name }) => ({
        url: "/api/v1/clst-accounts",
        params: { name },
      }),
      transformResponse: (
        response: AccountManagerAccount[] | { data: AccountManagerAccount[] },
      ) => {
        const accounts = Array.isArray(response)
          ? response
          : (response.data ?? []);
        return accounts.map((a) => ({ value: a.name, label: a.display_name }));
      },
      providesTags: ["AccountManagerAccounts"],
    }),
    /** Fetches full clst account list. */
    getClstAccounts: build.query<ClstAccount[], void>({
      query: () => ({ url: "/api/v1/clst-accounts" }),
      transformResponse: (response: ClstAccount[] | { data: ClstAccount[] }) =>
        Array.isArray(response) ? response : (response.data ?? []),
      providesTags: ["ClstAccounts"],
    }),
    /** Creates a new clst account. */
    createClstAccount: build.mutation<
      void,
      { name: string; display_name: string }
    >({
      query: (body) => ({
        url: "/api/v1/clst-accounts",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ClstAccounts", "AccountManagerAccounts", "CustomerConfigurations"],
    }),
    /** Updates a clst account's display_name. */
    updateClstAccount: build.mutation<
      void,
      { name: string; display_name: string }
    >({
      query: (body) => ({
        url: "/api/v1/clst-accounts",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ClstAccounts", "AccountManagerAccounts"],
    }),
    /** Fetches all customer configurations. */
    getCustomerConfigurations: build.query<CustomerConfiguration[], void>({
      query: () => ({ url: "/api/v1/customer-configurations" }),
      transformResponse: (
        response:
          | RawCustomerConfiguration[]
          | { data: RawCustomerConfiguration[] },
      ) => {
        const raw = Array.isArray(response) ? response : (response.data ?? []);
        return raw.map((c) => ({
          counterparty: c.Counterparty,
          all_in_tier: c.AllInTier ?? null,
          fee_tier: c.FeeTier ?? null,
          symbol_group: c.SymbolGroup ?? null,
          mode: c.Mode ?? null,
          update_user: c.UpdateUser ?? null,
          revision: c.Revision ?? null,
        }));
      },
      providesTags: ["CustomerConfigurations"],
    }),
    /** Updates a customer configuration's mode (Enabled/Disabled). */
    updateCustomerConfiguration: build.mutation<
      void,
      { name: string; mode: string }
    >({
      query: ({ name, mode }) => ({
        url: `/api/v1/customers/${name}/configuration`,
        method: "PUT",
        body: { mode },
      }),
      invalidatesTags: ["CustomerConfigurations"],
    }),
    /** Fetches house accounts from account-manager. */
    getHouseAccounts: build.query<SelectOption[], { name?: string }>({
      query: ({ name }) => ({
        url: "/api/v1/house-accounts",
        params: { name },
      }),
      transformResponse: (
        response: AccountManagerAccount[] | { data: AccountManagerAccount[] },
      ) => {
        const accounts = Array.isArray(response)
          ? response
          : (response.data ?? []);
        return accounts.map((a) => ({ value: a.name, label: a.display_name }));
      },
      providesTags: ["HouseAccounts"],
    }),
  }),
});

export const {
  useGetAccountManagerAccountsQuery,
  useGetClstAccountsQuery,
  useCreateClstAccountMutation,
  useUpdateClstAccountMutation,
  useGetCustomerConfigurationsQuery,
  useUpdateCustomerConfigurationMutation,
  useGetHouseAccountsQuery,
} = accountManagerEndpoints;
